/**
 * Segment builder (F3-I). Field/op/value rows over a static
 * manifest (mirror of Rust's curated map), preview (count + sample) and save.
 * Lists the organization's existing segments.
 */
import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Plus, Trash2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import {
  SEGMENT_FIELDS,
  type SegmentCondition,
  type SegmentFieldSpec,
  type SegmentTargetKind,
  useCreateSegment,
  useDeleteSegment,
  usePreviewSegment,
  useSegments,
} from "./api";
import { fieldLabel, opLabel } from "./labels";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

interface Row {
  field: string;
  op: SegmentCondition["op"];
  value: string;
}

const FALLBACK_SPEC: SegmentFieldSpec = { field: "stage", type: "text", ops: ["eq"] };
const firstSpec = (specs: SegmentFieldSpec[]): SegmentFieldSpec => specs[0] ?? FALLBACK_SPEC;
const firstRow = (specs: SegmentFieldSpec[]): Row => {
  const s = firstSpec(specs);
  return { field: s.field, op: s.ops[0] ?? "eq", value: "" };
};
const isTarget = (v: string): v is SegmentTargetKind => v === "leads" || v === "students";

/** Label for a preview sample row (name or id). */
function sampleLabel(row: Record<string, unknown>): string {
  const { name } = row;
  const { id } = row;
  if (typeof name === "string") {
    return name;
  }
  return typeof id === "string" ? id : "";
}

export function SegmentBuilder() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const segments = useSegments(activeOrgId);
  const createSeg = useCreateSegment();
  const deleteSeg = useDeleteSegment();
  const preview = usePreviewSegment();

  const [target, setTarget] = createSignal<SegmentTargetKind>("leads");
  const [name, setName] = createSignal("");
  const [rows, setRows] = createSignal<Row[]>([{ field: "stage", op: "eq", value: "" }]);

  const fields = createMemo(() => SEGMENT_FIELDS[target()]);

  const specFor = (f: string): SegmentFieldSpec =>
    fields().find((s) => s.field === f) ?? firstSpec(fields());

  const addRow = () => setRows((r) => [...r, firstRow(fields())]);
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // On target change, resets the rows to the first valid field.
  const changeTarget = (v: string) => {
    if (!isTarget(v)) {
      return;
    }
    setTarget(v);
    setRows([firstRow(SEGMENT_FIELDS[v])]);
  };

  const buildDefinition = () => ({
    all: rows()
      .filter((r) => r.value.trim() !== "")
      .map((r) => {
        const spec = specFor(r.field);
        let value: unknown = r.value.trim();
        if (spec.type === "int") {
          value = Number(r.value);
        } else if (r.op === "in") {
          value = r.value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        return { field: r.field, op: r.op, value };
      }),
  });

  const runPreview = () => {
    preview.mutate(
      { org: activeOrgId(), target: target(), definition: buildDefinition() },
      { onError: (e) => showToast(e.message, "error") },
    );
  };

  const save = () => {
    if (!name().trim()) {
      showToast(t(msg`Give the segment a name`), "error");
      return;
    }
    createSeg.mutate(
      { org: activeOrgId(), name: name().trim(), target: target(), definition: buildDefinition() },
      {
        onSuccess: () => {
          showToast(t(msg`Segment saved`), "success");
          setName("");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div
        class="space-y-4 rounded-xl border border-border bg-card p-5"
        data-testid="segment-builder"
      >
        <div class="flex gap-3">
          <div class="w-40">
            <Select
              options={[
                { value: "leads", label: t(msg`Leads`) },
                { value: "students", label: t(msg`Students`) },
              ]}
              value={target()}
              onChange={(v) => changeTarget(v)}
              aria-label={t(msg`Target`)}
            />
          </div>
          <input
            class={field}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder={t(msg`Segment name`)}
            data-testid="segment-name"
          />
        </div>

        <div class="space-y-2">
          <For each={rows()}>
            {(row, i) => {
              const spec = () => specFor(row.field);
              return (
                <div class="flex flex-wrap items-center gap-2">
                  <div class="w-44">
                    <Select
                      options={fields().map((s) => ({
                        value: s.field,
                        label: t(fieldLabel(s.field)),
                      }))}
                      value={row.field}
                      onChange={(v) => updateRow(i(), { field: v, op: specFor(v).ops[0] ?? "eq" })}
                      aria-label={t(msg`Field`)}
                    />
                  </div>
                  <div class="w-32">
                    <Select
                      options={spec().ops.map((o) => ({ value: o, label: t(opLabel(o)) }))}
                      value={row.op}
                      onChange={(v) => updateRow(i(), { op: v })}
                      aria-label={t(msg`Operator`)}
                    />
                  </div>
                  <Show
                    when={spec().type === "enum" && row.op !== "in"}
                    fallback={
                      <input
                        class={`${field} w-40`}
                        value={row.value}
                        onInput={(e) => updateRow(i(), { value: e.currentTarget.value })}
                        placeholder={row.op === "in" ? t(msg`comma,separated`) : t(msg`value`)}
                      />
                    }
                  >
                    <div class="w-40">
                      <Select
                        options={(spec().options ?? []).map((o) => ({ value: o, label: o }))}
                        value={row.value}
                        onChange={(v) => updateRow(i(), { value: v })}
                        aria-label={t(msg`Value`)}
                      />
                    </div>
                  </Show>
                  <button
                    type="button"
                    class="text-muted-foreground hover:text-destructive"
                    onClick={() => removeRow(i())}
                    aria-label={t(msg`Remove condition`)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
              );
            }}
          </For>
          <Button variant="ghost" class="gap-2" onClick={addRow}>
            <Plus class="h-4 w-4" /> {t(msg`Add condition`)}
          </Button>
        </div>

        <div class="flex gap-2 pt-2">
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={preview.isPending}
            data-testid="segment-preview"
          >
            {t(msg`Preview`)}
          </Button>
          <Button onClick={save} disabled={createSeg.isPending} data-testid="segment-save">
            {t(msg`Save segment`)}
          </Button>
        </div>

        <Show when={preview.data}>
          {(res) => (
            <div
              class="rounded-lg border border-border bg-muted/30 p-3"
              data-testid="segment-preview-result"
            >
              <p class="font-semibold">
                {t(msg`Matches`)}: {res().count}
              </p>
              <ul class="mt-1 space-y-0.5 text-sm text-muted-foreground">
                <For each={res().sample}>{(row) => <li>{sampleLabel(row)}</li>}</For>
              </ul>
            </div>
          )}
        </Show>
      </div>

      {/* Segmentos existentes */}
      <div class="space-y-2">
        <h3 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t(msg`Saved segments`)}
        </h3>
        <For
          each={segments.data ?? []}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No segments yet.`)}</p>}
        >
          {(seg) => (
            <div class="flex items-center justify-between rounded-lg border border-border bg-card p-3">
              <div>
                <div class="font-semibold">{seg.name}</div>
                <div class="text-xs text-muted-foreground">
                  {seg.target === "leads" ? t(msg`Leads`) : t(msg`Students`)} ·{" "}
                  {seg.definition.all.length} {t(msg`conditions`)}
                </div>
              </div>
              <button
                type="button"
                class="text-muted-foreground hover:text-destructive"
                onClick={() => deleteSeg.mutate(seg.id)}
                aria-label={t(msg`Delete segment`)}
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </div>
          )}
        </For>
      </div>
    </div>
  );
}
